import useUser from '../../../../shared/features/user/hooks/useUser';

import usePriceConfig from '../../../../shared/features/priceConfig/hooks/usePriceConfig';
import { getCurrencyForAreaUnit } from '../../../../shared/helpers/functions/currency';
import { StripeSubscriptionItemInput } from '../../../../shared/graphql/graphql';
import rootStore from '../../rootStore/RootStore';
import { Plan } from '../../../../shared/helpers/constants/plans';

export default function useStripeSubscription() {
  const { isCombinedDataLoaded: isCustomerLoaded, customer } =
    useUser(rootStore);
  const { isLoaded: isConfigLoaded, config } = usePriceConfig(rootStore);
  const { checkoutStore, areaUnitStore } = rootStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);

  const saveSubscription = async ({
    annual,
    monthly,
    promocode,
  }: {
    annual?: {
      area: string;
      seats: string;
      planet: string;
    };
    monthly?: boolean;
    promocode?: string;
  }) => {
    if (
      !isCustomerLoaded ||
      !isConfigLoaded ||
      checkoutStore.subscription.inProgress
    ) {
      return null;
    }

    const items: StripeSubscriptionItemInput[] = [];
    let plan: Plan.monthly | Plan.annual;

    if (annual) {
      const annualArea = parseInt(annual.area, 10);
      const annualSeats = parseInt(annual.seats, 10);
      const annualPlanetArea = parseInt(annual.planet, 10);

      const annualPrices = config?.AnnualSubscription;
      const areaPriceId =
        annualPrices?.FullAccessToEssentialFunctionalities?.prices[currency]
          ?.priceId;
      const seatPriceId =
        annualPrices?.AdditionalUserSeat?.prices[currency]?.priceId;
      const planetPriceId =
        annualPrices?.PlanetImagery?.prices[currency]?.priceId;

      if (!areaPriceId || !seatPriceId || !planetPriceId) {
        throw new Error(
          '[useStripeSubscription][saveSubscription] Annual plan prices are missing.',
        );
      }

      plan = Plan.annual;

      items.push({ price: areaPriceId, quantity: annualArea });
      items.push({ price: seatPriceId, quantity: annualSeats });
      items.push({ price: planetPriceId, quantity: annualPlanetArea });
    } else if (monthly) {
      const planConfig = config?.MonthlyPayAsYouGo;

      if (!planConfig?.BaseMonthlyFee?.prices[currency]?.priceId) {
        throw new Error(
          '[useStripeSubscription][saveSubscription] Monthly base fee price is missing.',
        );
      }

      plan = Plan.monthly;

      Object.values(planConfig).forEach((product) => {
        const priceId = product?.prices[currency]?.priceId;

        if (priceId) {
          items.push({ price: priceId });
        }
      });
    } else {
      throw new Error(
        '[useStripeSubscription][saveSubscription] Invalid plan.',
      );
    }

    return checkoutStore.saveStripeSubscription({
      plan,
      customerId: customer?.id ?? '',
      items,
      promocode,
      areaUnit,
    });
  };

  return {
    clientSecret: checkoutStore.subscription.clientSecret,
    subscriptionId: checkoutStore.subscription.id,
    isSubscriptionSaving: checkoutStore.subscription.inProgress,
    saveSubscription,
  };
}
